import { AnyAction, createReducer } from '@reduxjs/toolkit';
import {
	changeValueMappingTableProfilesFilter,
	changeValueMappingTableProfilesPageSize,
	changeValueMappingTableProfilesSort,
	checkOverridesValueMappingProfile,
	checkOverridesValueMappingProfilesRejected,
	checkOverridesValueMappingProfilesResolved,
	checkSensorDeletionsValueMappingProfile,
	checkSensorDeletionsValueMappingProfilesRejected,
	checkSensorDeletionsValueMappingProfilesResolved,
	createValueMappingProfile,
	createValueMappingProfilesRejected,
	createValueMappingProfilesResolved,
	deleteValuemappingProfile,
	deleteValuemappingProfileRejected,
	deleteValuemappingProfileResolved,
	getSingleValueMappingProfile,
	getSingleValueMappingProfileResolved,
	getValueMappingProfiles,
	getValueMappingProfilesRejected,
	getValueMappingProfilesResolved,
	getValuemappingSensorsList,
	getValuemappingSensorsListRejected,
	getValuemappingSensorsListResolved,
	setValuemappingProfileDurationDialogOpen,
	updateValueMappingProfile,
	updateValueMappingProfileRejected,
	updateValueMappingProfileResolved,
} from '@Actions/valueMapping';
import {
	CheckOverridesResponse,
	CheckSensorDeletionsResponse,
	DurationDialogOperation,
	GetValuemappingProfilesRequest,
	GetValuemappingProfilesResponse,
	ValuemappingManagerStoreState,
} from '@Types';
import { SENSOR_TYPE_LIST } from '@innovyze/shared-utils';
import {
	MultiselectOption,
	MultiselectOptionWithSensorType,
} from '@Types/multiselect.types';
import { ValuemappingProfileFormShape } from '@Types/valuemappingForm.types';

const sensorTypeOptionsInitialState: MultiselectOption<string>[] =
	SENSOR_TYPE_LIST.map(sensorType => ({
		key: sensorType,
		value: sensorType,
	}));

export const initialState: ValuemappingManagerStoreState = {
	initialized: true,
	valueMappingDetail: {
		profileName: '',
		sensorIds: [],
		sensorTypes: [],
		mappings: [],
		mappingMethods: ['Last Known Value', 'Custom'],
		multiselect: {
			sensorTypes: { options: sensorTypeOptionsInitialState },
			sensorIds: { initialized: false, options: [] },
		},
		deletableSensorsCount: null,
	},
	valueMappingProfiles: {
		list: [],
		pagination: { current: 0, next: 0, pages: 0, previous: 0, total: 0 },
		tableState: {
			limitIndex: 1,
			searchQuery: '',
			page: 1,
			sort: {
				field: 'valueMappingProfileModifiedAt',
				sortOrder: 'desc',
			},
		},
		filters: {
			sensorType: {
				selected: 'All sensor types',
				options: ['All sensor types', 'sensor_id', 'sensor_type'],
			},
		},
	},
	overrides: { list: [] },
	durationDialogOperation: 'NONE',
};

interface ValuemappingProfiles {
	[x: string]: (
		state: ValuemappingManagerStoreState,
		action: AnyAction,
	) => ValuemappingManagerStoreState | undefined | void;
}

const reducer: ValuemappingProfiles = {
	// ? Read one
	[getSingleValueMappingProfile.toString()]: state => {
		state.valueMappingDetail = initialState.valueMappingDetail; // ? reset details
		state.initialized = false;
	},
	[getSingleValueMappingProfileResolved.toString()]: (state, action) => {
		const payload = action.payload as ValuemappingProfileFormShape;
		state.valueMappingDetail.profileName = payload.profileName;
		state.valueMappingDetail.sensorIds = payload.sensorIds;
		state.valueMappingDetail.sensorTypes = payload.sensorTypes;
		state.valueMappingDetail.mappings = payload.mappings;
		state.initialized = true;
	},
	// ? Read all
	[getValueMappingProfiles.toString()]: state => {
		state.initialized = false;
	},
	[getValueMappingProfilesResolved.toString()]: (state, action) => {
		const payload: GetValuemappingProfilesResponse =
			action.payload as GetValuemappingProfilesResponse;
		state.initialized = true;
		state.valueMappingProfiles.list = payload.rows;
		state.valueMappingProfiles.pagination = payload.pagination;
		state.valueMappingProfiles.tableState.page = payload.pagination.current;
	},
	[getValueMappingProfilesRejected.toString()]: state => {
		state.initialized = true;
	},
	// ? Check overrides
	[checkOverridesValueMappingProfile.toString()]: state => {
		state.initialized = false;
		state.overrides.list = [];
		state.durationDialogOperation = 'NONE';
	},
	[checkOverridesValueMappingProfilesResolved.toString()]: (
		state,
		action,
	) => {
		const payload = action.payload as CheckOverridesResponse['rows'];
		state.initialized = true;
		state.overrides.list = payload;
	},
	[checkOverridesValueMappingProfilesRejected.toString()]: state => {
		state.initialized = true;
		state.overrides.list = [];
		state.durationDialogOperation = 'NONE';
	},
	// ? Creation
	[createValueMappingProfile.toString()]: state => {
		state.initialized = false;
		state.overrides.list = [];
	},
	[createValueMappingProfilesResolved.toString()]: state => {
		state.initialized = true;
	},
	[createValueMappingProfilesRejected.toString()]: state => {
		state.initialized = true;
	},
	// ? Pagination
	[changeValueMappingTableProfilesFilter.toString()]: (state, action) => {
		const payload = action.payload as { name: string; value: string };
		state.valueMappingProfiles.filters[payload.name].selected =
			payload.value;
	},
	[changeValueMappingTableProfilesSort.toString()]: (state, action) => {
		const payload =
			action.payload as GetValuemappingProfilesRequest['sort'];
		state.valueMappingProfiles.tableState.sort = payload;
	},
	[changeValueMappingTableProfilesPageSize.toString()]: (state, action) => {
		const payload: number = action.payload as number;
		state.valueMappingProfiles.tableState.limitIndex = payload;
	},
	// ? Update
	[updateValueMappingProfile.toString()]: state => {
		state.initialized = false;
	},
	[updateValueMappingProfileResolved.toString()]: (state, action) => {
		const payload = action.payload as ValuemappingProfileFormShape;
		state.valueMappingDetail.profileName = payload.profileName;
		state.valueMappingDetail.sensorIds = payload.sensorIds;
		state.valueMappingDetail.sensorTypes = payload.sensorTypes;
		state.valueMappingDetail.mappings = payload.mappings;
		state.initialized = true;
		state.overrides.list = [];
	},
	[updateValueMappingProfileRejected.toString()]: state => {
		state.initialized = true;
	},
	// ? Delete
	[deleteValuemappingProfile.toString()]: state => {
		state.initialized = false;
	},
	[deleteValuemappingProfileResolved.toString()]: (state, action) => {
		const payload: string = action.payload as string;
		const idx = state.valueMappingProfiles.list.findIndex(
			el => el.id === payload,
		);
		if (idx >= 0) state.valueMappingProfiles.list.splice(idx, 1);
		state.initialized = true;
	},
	[deleteValuemappingProfileRejected.toString()]: state => {
		state.initialized = true;
	},
	// ? Sensor Ids
	[getValuemappingSensorsList.toString()]: state => {
		state.valueMappingDetail.multiselect.sensorIds.initialized = false;
	},
	[getValuemappingSensorsListResolved.toString()]: (state, action) => {
		const payload: MultiselectOptionWithSensorType[] =
			action.payload as MultiselectOptionWithSensorType[];
		state.valueMappingDetail.multiselect.sensorIds.initialized = true;
		state.valueMappingDetail.multiselect.sensorIds.options = payload;
	},
	[getValuemappingSensorsListRejected.toString()]: state => {
		state.valueMappingDetail.multiselect.sensorIds.initialized = true;
	},
	// ? Check deletable sensors count
	[checkSensorDeletionsValueMappingProfile.toString()]: state => {
		state.valueMappingDetail.deletableSensorsCount = null;
		state.initialized = false;
	},
	[checkSensorDeletionsValueMappingProfilesResolved.toString()]: (
		state,
		action,
	) => {
		const payload: CheckSensorDeletionsResponse =
			action.payload as CheckSensorDeletionsResponse;
		state.valueMappingDetail.deletableSensorsCount = payload.count;
		state.initialized = true;
	},
	[checkSensorDeletionsValueMappingProfilesRejected.toString()]: state => {
		state.valueMappingDetail.deletableSensorsCount = null;
		state.initialized = true;
	},
	// ? Duration dialog
	[setValuemappingProfileDurationDialogOpen.toString()]: (state, action) => {
		const payload: DurationDialogOperation =
			action.payload as DurationDialogOperation;
		state.durationDialogOperation = payload;
	},
};

export default createReducer(initialState, reducer);
